import {
  Accessify,
  type AccessifyConfig,
  type Lang,
  type Position,
  type TriggerScheme,
  type WidgetSize,
} from "../core/index";

export { Accessify };
export type { AccessifyConfig } from "../core/index";

/**
 * Searches the DOM for the script element that loaded Accessify.
 */
export function findAccessifyScript(): HTMLScriptElement | null {
  if (typeof document === "undefined") return null;

  // 1. Check document.currentScript (works for non-deferred synchronous scripts)
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }

  // 2. Query for explicit data-accessify tag or matching src path
  return (
    document.querySelector<HTMLScriptElement>("script[data-accessify]") ??
    document.querySelector<HTMLScriptElement>('script[src*="accessify"]')
  );
}

/**
 * Extracts AccessifyConfig from HTML data attributes on a <script> element.
 * Supported attributes:
 * - data-position="bottom-right|bottom-left|top-right|top-left"
 * - data-lang="en|es|fr|de|pt|ar"
 * - data-theme="light|dark|system" or data-color-scheme="light|dark|system"
 * - data-size="S|M|L"
 * - data-trigger-scheme="blue|light|dark"
 * - data-primary-color="#1A56DB" or data-color="#1A56DB"
 * - data-statement-url="https://example.com/accessibility"
 * - data-persistence="true|false"
 */
export function parseConfigFromScript(
  script: HTMLScriptElement | null,
): AccessifyConfig {
  if (!script) return {};

  const ds = script.dataset;
  const config: AccessifyConfig = {};

  if (
    ds.position &&
    ["bottom-right", "bottom-left", "top-right", "top-left"].includes(
      ds.position,
    )
  ) {
    config.position = ds.position as Position;
  }

  if (ds.lang && ["en", "es", "fr", "de", "pt", "ar"].includes(ds.lang)) {
    config.lang = ds.lang as Lang;
  }

  const theme = ds.theme || ds.colorScheme;
  if (theme === "dark" || theme === "light") {
    config.colorScheme = theme;
  }

  if (ds.size) {
    const s = ds.size.toUpperCase();
    if (["S", "M", "L"].includes(s)) {
      config.size = s as WidgetSize;
    }
  }

  if (
    ds.triggerScheme &&
    ["auto", "dark", "light", "blue"].includes(ds.triggerScheme)
  ) {
    config.triggerScheme = ds.triggerScheme as TriggerScheme;
  }

  const primary = ds.primaryColor || ds.color;
  if (primary) {
    config.theme = { primary };
  }

  if (ds.brandTitle) {
    config.brandTitle = ds.brandTitle;
  }

  if (ds.persistence !== undefined) {
    config.persistence = ds.persistence !== "false";
  }

  return config;
}

/**
 * Auto-initializes the Accessify widget in browser environments.
 */
export function autoInit(): void {
  if (typeof window === "undefined" || typeof document === "undefined")
    return; // Always expose constructor on window
  (window as any).Accessify = Accessify;

  const script = findAccessifyScript();
  if (script && script.dataset.autoInit === "false") {
    return;
  }

  const config = parseConfigFromScript(script);

  const mount = () => {
    // Only mount if an instance has not already been created
    if (!Accessify.getInstance()) {
      Accessify.init(config);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount, { once: true });
  } else {
    mount();
  }
}

// Automatically invoke autoInit when bundle is evaluated in browser
autoInit();

export default Accessify;
